import React, { useRef, useState } from 'react';
import './index.scss';
import useCropBox from '../../../hooks/useCropBox';
import Button from '../../Button';
import ProgressBar from '../../ProgressBar';

const MIN_SIZE = 40;

export default function ImageCropper() {
  const [image, setImage] = useState(null);
  const [imgSize, setImgSize] = useState({ width: 500, height: 400 });
  const imgRef = useRef();
  const cropRef = useRef();

  // 图片加载后获取实际渲染宽高
  const handleImgLoad = () => {
    if (imgRef.current) {
      // 获取图片的实际显示尺寸
      const img = imgRef.current;
      const rect = img.getBoundingClientRect();
      const newImgSize = { 
        width: Math.round(rect.width), 
        height: Math.round(rect.height) 
      };
      setImgSize(newImgSize);
    }
  };

  // 裁剪框 hook
  const {
    crop,
    setCrop,
    dragging,
    resizing,
    offset,
    onMouseDown,
    onMouseMove,
    onMouseUp
  } = useCropBox({ initCrop: { x: 50, y: 50, w: 200, h: 200 }, minSize: 40, bound: imgSize });

  // 处理本地文件上传
  const handleFile = e => {
    const file = e.target.files[0];
    if (file) {
      setImage(URL.createObjectURL(file));
    }
  };

  // 保存裁剪图片
  const handleSave = () => {
    try {
      const canvas = document.createElement('canvas');
      canvas.width = crop.w;
      canvas.height = crop.h;
      const ctx = canvas.getContext('2d');
      const img = imgRef.current;
      
      // 计算图片实际显示尺寸和原图的比例
      const scaleX = img.naturalWidth / img.width;
      const scaleY = img.naturalHeight / img.height;
      
      // 尝试绘制图片到canvas
      ctx.drawImage(
        img,
        crop.x * scaleX,
        crop.y * scaleY,
        crop.w * scaleX,
        crop.h * scaleY,
        0,
        0,
        crop.w,
        crop.h
      );
      
      // 尝试导出数据URL
      try {
        const dataURL = canvas.toDataURL();
        const link = document.createElement('a');
        link.download = 'cropped.png';
        link.href = dataURL;
        link.click();
      } catch (error) {
        console.error('无法保存图片:', error);
        alert('保存图片时发生错误，请重试。');
      }
    } catch (error) {
      console.error('保存图片失败:', error);
      alert('保存图片时发生错误，请重试。');
    }
  };

  // 预览canvas
  const renderPreview = () => {
    if (!image) return null;
    const img = imgRef.current;
    if (!img) return null;
    
    try {
      const scaleX = img.naturalWidth / img.width;
      const scaleY = img.naturalHeight / img.height;
      const previewW = 160;
      const previewH = Math.round((crop.h / crop.w) * previewW);
      const canvas = document.createElement('canvas');
      canvas.width = previewW;
      canvas.height = previewH;
      const ctx = canvas.getContext('2d');
      
      // 尝试绘制图片到canvas
      ctx.drawImage(
        img,
        crop.x * scaleX,
        crop.y * scaleY,
        crop.w * scaleX,
        crop.h * scaleY,
        0,
        0,
        previewW,
        previewH
      );
      
      // 尝试导出数据URL
      try {
        const dataURL = canvas.toDataURL();
        return (
          <img 
            src={dataURL} 
            alt="预览" 
            style={{ 
              width: previewW, 
              height: previewH, 
              border: '1px solid rgba(0, 255, 0, 0.3)',
              borderRadius: '4px'
            }} 
          />
        );
      } catch (error) {
        console.error('无法生成预览图片:', error);
        return (
          <div 
            style={{ 
              width: previewW, 
              height: previewH, 
              border: '1px solid rgba(0, 255, 0, 0.3)',
              borderRadius: '4px',
              background: 'rgba(0, 0, 0, 0.3)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'rgba(0, 255, 0, 0.6)',
              fontSize: '0.8rem',
              textAlign: 'center'
            }}
          >
            预览生成失败
          </div>
        );
      }
    } catch (error) {
      console.error('预览生成失败:', error);
      return (
        <div 
          style={{ 
            width: 160, 
            height: 120, 
            border: '1px solid rgba(0, 255, 0, 0.3)',
            borderRadius: '4px',
            background: 'rgba(0, 0, 0, 0.3)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'rgba(0, 255, 0, 0.6)',
            fontSize: '0.8rem',
            textAlign: 'center'
          }}
        >
          预览生成失败
        </div>
      );
    }
  };

  return (
    <div onMouseMove={onMouseMove} onMouseUp={onMouseUp} className="imagecropper_container">
      <div className="imagecropper_left">
        {/* 图片输入区域 */}
        <div className="imagecropper_input_section">
          {/* 文件上传区域 */}
          <div className="imagecropper_file_upload">
            <input type="file" accept="image/*" onChange={handleFile} className="imagecropper_input_file" />
            <div className="imagecropper_file_status">
              {image ? '图片已加载' : '未选择任何文件'}
            </div>
          </div>
        </div>
        
        {/* 图片裁剪区域 */}
        <div className="imagecropper_canvas" style={{ width: imgSize.width, height: imgSize.height }}>
          {image && (
            <>
              <img
                ref={imgRef}
                src={image}
                alt="待裁剪"
                className="imagecropper_img"
                draggable={false}
                onLoad={handleImgLoad}
              />
              <div
                ref={cropRef}
                className="imagecropper_crop"
                style={{ left: crop.x, top: crop.y, width: crop.w, height: crop.h }}
                onMouseDown={onMouseDown}
              >
                {/* 四角拖拽点 */}
                {['nw','ne','sw','se'].map(corner => {
                  const style = {
                    position: 'absolute',
                    width: 12,
                    height: 12,
                    background: '#00ff00',
                    borderRadius: 6,
                    zIndex: 3,
                    cursor: `${corner}-resize`,
                    boxShadow: '0 0 8px #00ff00'
                  };
                  if (corner === 'nw') Object.assign(style, { left: -6, top: -6 });
                  if (corner === 'ne') Object.assign(style, { right: -6, top: -6 });
                  if (corner === 'sw') Object.assign(style, { left: -6, bottom: -6 });
                  if (corner === 'se') Object.assign(style, { right: -6, bottom: -6 });
                  return <div key={corner} data-corner={corner} className="imagecropper_corner" style={style} onMouseDown={onMouseDown} />;
                })}
              </div>
            </>
          )}
        </div>
        
        {/* 保存按钮 */}
        {image && (
          <div className="imagecropper_actions">
            <Button onClick={handleSave} className="imagecropper_btn_save">保存裁剪结果</Button>
          </div>
        )}
      </div>
      
      {/* 预览区 */}
      <div className="imagecropper_preview_area">
        <div className="imagecropper_preview_title">裁剪预览</div>
        <div className="imagecropper_preview_box">
          {renderPreview()}
        </div>
      </div>
    </div>
  );
} 